export const RUNTIME_DEV_MODE_KEY = 'VITE_DEV_MODE';
export const RUNTIME_DEV_MODE_FALLBACK = 'true';

const isTemplatePlaceholder = (value: string): boolean =>
  value.startsWith('${') && value.endsWith('}');

type RuntimeConfigRecord = Record<string, string | undefined>;

type WindowWithRuntimeConfig = Window & {
  __RUNTIME_CONFIG__?: RuntimeConfigRecord;
};

/**
 * Raw `VITE_DEV_MODE` string with the same resolution order as `getEnvVar` in
 * apps/periverse (runtime config, then build-time env, then process.env, then fallback).
 */
export function getViteDevModeRaw(): string {
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as WindowWithRuntimeConfig)
      .__RUNTIME_CONFIG__?.[RUNTIME_DEV_MODE_KEY];
    if (
      runtimeValue &&
      typeof runtimeValue === 'string' &&
      runtimeValue !== `\${${RUNTIME_DEV_MODE_KEY}}` &&
      runtimeValue.trim() !== '' &&
      !isTemplatePlaceholder(runtimeValue)
    ) {
      return runtimeValue;
    }
  }

  const importMetaEnv = (
    globalThis as typeof globalThis & {
      import?: { meta?: { env?: Record<string, string | undefined> } };
    }
  ).import?.meta?.env;
  const viteValue = importMetaEnv?.[RUNTIME_DEV_MODE_KEY];
  if (typeof viteValue === 'string' && viteValue.trim() !== '') {
    return viteValue;
  }

  if (typeof process !== 'undefined') {
    const processValue = process.env?.[RUNTIME_DEV_MODE_KEY];
    if (processValue && processValue.trim() !== '') {
      return processValue;
    }
  }

  return RUNTIME_DEV_MODE_FALLBACK;
}

/**
 * True when runtime (or build) config sets `VITE_DEV_MODE` to the string `'true'`.
 */
export function isRuntimeDevMode(): boolean {
  return getViteDevModeRaw() === 'true';
}
